/**
 * Entitlements in the UI.
 *
 * The backend is the boundary — every gated route re-checks. This exists so the
 * interface tells the truth *before* the click: a feature the plan does not
 * include should read as an upgrade, not as a button that fails.
 *
 * Feature keys are the same strings the backend registry declares. When the two
 * disagree, the backend wins and the user sees a 402 they could have been
 * warned about, which is why `useFeature` reads from the live
 * `/v1/me/entitlements` response rather than from a copy hardcoded here.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useApi } from './provider';

export type PlanTier = 'starter' | 'professional' | 'enterprise';

export interface EntitlementsResponse {
  plan: PlanTier;
  suspended: boolean;
  features: string[];
  limits: Record<string, number | null>;
}

export interface Entitlements {
  plan: PlanTier;
  suspended: boolean;
  /** Whether a feature key is available on the current plan. */
  has(featureKey: string): boolean;
  /** `null` means unlimited; `undefined` means the key was never declared. */
  limit(limitKey: string): number | null | undefined;
  /** `current < cap`, matching the backend check exactly. */
  withinLimit(limitKey: string, current: number): boolean;
}

const EntitlementsContext = createContext<Entitlements | null>(null);

const PLAN_ORDER: PlanTier[] = ['starter', 'professional', 'enterprise'];

export function planAtLeast(plan: PlanTier, required: PlanTier): boolean {
  return PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(required);
}

export function useEntitlementsQuery(): UseQueryResult<EntitlementsResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['entitlements'],
    queryFn: ({ signal }) => api.get<EntitlementsResponse>('/v1/me/entitlements', signal),
    // Plans change when someone upgrades, not between renders. Long enough to
    // stay off the hot path, short enough that an upgrade lands without a
    // reload.
    staleTime: 5 * 60_000,
  });
}

export function EntitlementsProvider({
  data,
  children,
}: {
  data: EntitlementsResponse;
  children: ReactNode;
}) {
  const value = useMemo<Entitlements>(() => {
    const features = new Set(data.features);
    return {
      plan: data.plan,
      suspended: data.suspended,
      has: (key) => !data.suspended && features.has(key),
      limit: (key) => (key in data.limits ? data.limits[key] : undefined),
      withinLimit: (key, current) => {
        const cap = key in data.limits ? data.limits[key] : undefined;
        if (cap === null || cap === undefined) return true;
        return current < cap;
      },
    };
  }, [data]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export function useEntitlements(): Entitlements {
  const value = useContext(EntitlementsContext);
  if (value === null) {
    throw new Error('useEntitlements must be used inside an <EntitlementsProvider>');
  }
  return value;
}

export function useFeature(featureKey: string): boolean {
  const entitlements = useEntitlements();
  return entitlements.has(featureKey);
}

/** Whether creating one more of something is allowed right now. */
export function useWithinLimit(limitKey: string, current: number): boolean {
  const entitlements = useEntitlements();
  const check = useCallback(
    () => entitlements.withinLimit(limitKey, current),
    [entitlements, limitKey, current],
  );
  return check();
}

/**
 * Render `children` only when the plan includes the feature.
 *
 * `fallback` is for the upgrade nudge. Passing nothing hides the feature
 * entirely, which is right for navigation and wrong for a primary action — a
 * button that vanishes reads as a bug, an explained one reads as a plan.
 */
export function Gated({
  feature,
  children,
  fallback = null,
}: {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return useFeature(feature) ? <>{children}</> : <>{fallback}</>;
}
